import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, ArrowRight, PlusCircle, ArrowUpRight, Lock, Clock, Wallet,
  CheckCircle2, AlertTriangle, FileText } from "lucide-react";
import { Logo, TelegramButton, fmtINR } from "@/components/common";
import { api } from "@/lib/api";

function Section({ id, children, className = "" }) {
  return <section id={id} className={`px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto ${className}`}>{children}</section>;
}

export default function Landing() {
  const [settings, setSettings] = useState({});
  useEffect(() => { api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {}); }, []);

  const features = [
    { icon: Lock, title: "Server-backed ledger", desc: "Every USDT movement is atomic, audited and immutable. We never trust client-side balances." },
    { icon: Clock, title: "10-minute confirmation", desc: "A live countdown begins only after staff mark your INR payout as sent. You stay in control." },
    { icon: Wallet, title: "Separate balances", desc: "Available, held and receivable amounts are always shown distinctly — never mixed together." },
    { icon: ShieldCheck, title: "Verified deposits", desc: "Deposits are credited only after authorized staff verify your TXID and screenshot." },
  ];

  return (
    <div className="bg-white">
      {/* Navbar */}
      <header className="sticky top-0 z-40 backdrop-blur-md bg-white/85 border-b border-slate-200">
        <div className="flex items-center justify-between px-4 sm:px-6 lg:px-8 h-16 max-w-7xl mx-auto">
          <Logo />
          <nav className="hidden md:flex items-center gap-7 text-sm font-medium text-slate-600">
            <a href="#features" className="hover:text-slate-900">Features</a>
            <a href="#how" className="hover:text-slate-900">How It Works</a>
            <a href="#flows" className="hover:text-slate-900">Add & Sell</a>
            <a href="#legal" className="hover:text-slate-900">Legal</a>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login" data-testid="nav-login-button"
              className="px-4 py-2 rounded-full text-sm font-semibold text-slate-700 hover:bg-slate-100 transition">Login</Link>
            <Link to="/signup" data-testid="nav-get-started-button"
              className="px-4 py-2 rounded-full text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 transition inline-flex items-center gap-1.5">
              Get Started <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <Section className="pt-16 pb-20 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <span className="inline-flex items-center gap-2 text-xs font-semibold text-blue-700 bg-blue-50 px-3 py-1.5 rounded-full">
            <ShieldCheck className="w-4 h-4" /> Transparent USDT transactions
          </span>
          <h1 className="font-heading mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
            Manage Your USDT.<br /><span className="text-blue-600">Track Every Transaction.</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl">
            TrustFlow is a transaction management platform to deposit USDT, sell it for INR and follow
            every payout with a clear, auditable record — no guesswork, no hidden balances.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link to="/signup" data-testid="hero-get-started-button"
              className="px-6 py-3 rounded-full text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 transition inline-flex items-center gap-2 shadow-lg shadow-blue-600/25">
              Create account <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/login" className="px-6 py-3 rounded-full text-sm font-semibold border border-slate-300 text-slate-700 hover:bg-slate-50 transition">
              I already have an account
            </Link>
            <TelegramButton url={settings.telegram_url} />
          </div>
          {settings.global_rate && (
            <div className="mt-8 inline-flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3">
              <span className="text-xs uppercase tracking-wider text-slate-500 font-medium">Reference sell rate</span>
              <span className="font-mono font-bold text-slate-900" data-testid="landing-reference-rate">{fmtINR(settings.global_rate)} / USDT</span>
            </div>
          )}
        </div>
        <div className="relative">
          <div className="rounded-3xl overflow-hidden border border-slate-200 shadow-2xl shadow-blue-600/10">
            <img src="https://images.unsplash.com/photo-1551650975-87deedd944c3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
              alt="TrustFlow dashboard" className="w-full h-full object-cover" />
          </div>
        </div>
      </Section>

      {/* Features */}
      <div className="bg-slate-50 py-20">
        <Section id="features">
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">Built for trust</h2>
          <p className="mt-3 text-slate-600 max-w-2xl">Financial safety is enforced on the backend, not just in the interface.</p>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {features.map((f) => (
              <div key={f.title} className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm hover:shadow-md transition">
                <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center"><f.icon className="w-5 h-5 text-blue-600" /></div>
                <h3 className="font-heading mt-4 font-semibold text-slate-900">{f.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </Section>
      </div>

      {/* How it works */}
      <Section id="how" className="py-20">
        <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">How It Works</h2>
        <div className="mt-10 grid md:grid-cols-4 gap-5">
          {[
            { n: "01", t: "Add USDT", d: "Send USDT to the verified TRC20/BEP20 address and submit your TXID + screenshot." },
            { n: "02", t: "Get verified", d: "Staff verify your transaction and credit the exact amount to your wallet." },
            { n: "03", t: "Sell USDT", d: "Create a sell order at a locked rate. The amount is reserved instantly." },
            { n: "04", t: "Receive INR", d: "Staff pay you in INR; confirm receipt within the 10-minute window." },
          ].map((s) => (
            <div key={s.n} className="relative bg-white border border-slate-200 rounded-2xl p-6">
              <span className="font-mono text-3xl font-bold text-blue-100">{s.n}</span>
              <h3 className="font-heading font-semibold text-slate-900 mt-2">{s.t}</h3>
              <p className="mt-2 text-sm text-slate-600">{s.d}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Add & Sell */}
      <div className="bg-slate-900 py-20" id="flows">
        <Section>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-slate-800 rounded-2xl p-8 border border-slate-700">
              <div className="w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center"><PlusCircle className="w-6 h-6 text-white" /></div>
              <h3 className="font-heading text-xl font-bold text-white mt-5">Add USDT</h3>
              <p className="mt-3 text-slate-300 text-sm leading-relaxed">
                Deposit USDT over TRC20 or BEP20 to our verified receiving address. Scan the QR, copy the
                address, send, then submit your transaction hash and screenshot. Your deposit is credited only after verification.
              </p>
              <ul className="mt-4 space-y-2 text-sm text-slate-300">
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" /> Supported: TRC20 & BEP20</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" /> Exactly-once credit via atomic ledger</li>
              </ul>
            </div>
            <div className="bg-slate-800 rounded-2xl p-8 border border-slate-700">
              <div className="w-12 h-12 rounded-xl bg-emerald-600 flex items-center justify-center"><ArrowUpRight className="w-6 h-6 text-white" /></div>
              <h3 className="font-heading text-xl font-bold text-white mt-5">Sell USDT</h3>
              <p className="mt-3 text-slate-300 text-sm leading-relaxed">
                Create a sell order and the USDT is reserved immediately at a locked rate. Staff pay your INR
                via UPI or bank transfer, and you confirm each payment within a 10-minute window.
              </p>
              <ul className="mt-4 space-y-2 text-sm text-slate-300">
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" /> Locked rate snapshot per order</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" /> Independent timer per partial payment</li>
              </ul>
            </div>
          </div>
        </Section>
      </div>

      {/* Legal */}
      <Section id="legal" className="py-20">
        <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Terms, Privacy & Risk</h2>
        <div className="mt-8 grid md:grid-cols-3 gap-5">
          {[
            { icon: FileText, t: "Terms & Conditions", d: "Rates are locked at order creation. Completed transactions are final. Services may be paused for maintenance." },
            { icon: ShieldCheck, t: "Privacy Policy", d: "We collect only what is needed to process transactions. Payout details are protected and access-controlled." },
            { icon: AlertTriangle, t: "Risk Disclosure", d: "Crypto values are volatile and transfers are irreversible. Never share private keys or seed phrases with anyone." },
          ].map((c) => (
            <div key={c.t} className="bg-white border border-slate-200 rounded-2xl p-6">
              <c.icon className="w-6 h-6 text-blue-600" />
              <h3 className="font-heading font-semibold text-slate-900 mt-3">{c.t}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{c.d}</p>
            </div>
          ))}
        </div>
        <div className="mt-8 bg-amber-50 border border-amber-200 rounded-xl p-5 text-sm text-amber-900">
          <strong>Compliance note:</strong> This platform is a transaction management tool. Before handling real customer funds,
          obtain professional review of applicable Indian legal, tax, KYC/AML, banking and payment-provider requirements.
          No regulatory approval is claimed.
        </div>
      </Section>

      {/* Footer */}
      <footer className="bg-slate-50 border-t border-slate-200 py-12">
        <Section className="flex flex-col sm:flex-row items-center justify-between gap-6">
          <Logo />
          <div className="flex items-center gap-6 text-sm text-slate-500">
            <a href="#legal" className="hover:text-slate-900">Terms</a>
            <a href="#legal" className="hover:text-slate-900">Privacy</a>
            <a href="#legal" className="hover:text-slate-900">Risk</a>
          </div>
          <TelegramButton url={settings.telegram_url} testid="footer-telegram-button" />
        </Section>
        <p className="text-center text-xs text-slate-400 mt-8">© {new Date().getFullYear()} TrustFlow. All rights reserved.</p>
      </footer>
    </div>
  );
}
